import { ConsultantNode } from '../types/career';
import { AutoPathwayConfig, MonthlySimulationStep, SimulationResult } from '../types/simulation';
import { evaluateTreeQualifications } from '../math/qualification';
import { calculateNodePayout, DEFAULT_USD_EXCHANGE_RATE } from '../math/payout';
import { cloneTree, countActiveConsultants, countTotalConsultants, flattenTree } from '../utils/treeUtils';

export const DEFAULT_AUTO_PATHWAY_CONFIG: AutoPathwayConfig = {
  months: 12,
  newbieMeetingsPerMonth: 15,
  leaderMeetingsPerMonth: 8,
  conversionRate: 0.10,
  meetingsPerContract: 10,
  contractSizeAE: 50.0,
  averageContractAE: 50.0,
  recruitmentPace: 1.0,
  teamSeminarRecruitPercent: 20,
  downlineDuplicationFactor: 1.0,
  attritionRate: 0.10,
  preOnboardingAttritionRate: 0.30,
  usdExchangeRate: DEFAULT_USD_EXCHANGE_RATE,
  leaderCeaseLevel: 0,
  teamCeaseLevel: 0,
  commissionRateCoefficient: 1.0,
};

interface InternalConsultantState {
  recruitPool: number;
  meetingPool: number;
}

/**
 * Deterministic pseudo-random check for consultant dormancy based on node ID, month, and attrition rate.
 * Guarantees reproducible simulations without Math.random() jitter.
 */
function isNodeActiveDeterministic(nodeId: string, month: number, attritionRate: number): boolean {
  if (attritionRate <= 0) return true;
  if (attritionRate >= 1) return false;

  let hash = 0;
  const str = `${nodeId}-m${month}-attr`;
  for (let i = 0; i < str.length; i++) {
    hash = (hash << 5) - hash + str.charCodeAt(i);
    hash |= 0;
  }
  const unitRand = (Math.abs(hash) % 10000) / 10000;
  return unitRand >= attritionRate;
}

/**
 * Generates Ukrainian first names for realistic team visualization in Auto Pathway.
 */
const CONSULTANT_NAMES = [
  'Олександр', 'Олена', 'Михайло', 'Тетяна', 'Андрій', 'Наталія', 'Сергій', 'Юлія',
  'Дмитро', 'Оксана', 'Іван', 'Вікторія', 'Василь', 'Анна', 'Максим', 'Марія',
  'Роман', 'Ірина', 'Володимир', 'Світлана', 'Тарас', 'Катерина', 'Павло', 'Ольга',
  'Артем', 'Христина', 'Богдан', 'Дарина', 'Євген', 'Аліна', 'Віталій', 'Любов',
];

function getConsultantName(index: number): string {
  const name = CONSULTANT_NAMES[(index - 1) % CONSULTANT_NAMES.length];
  const cycle = Math.floor((index - 1) / CONSULTANT_NAMES.length);
  return cycle > 0 ? `${name} (${cycle + 1})` : name;
}

/**
 * Simulates the 1–60 month Career Pathway of a Starlife consultant.
 * Can start from Month 1 as a new consultant or continue from an existing initial structure.
 */
export function runAutoPathwaySimulation(
  customConfig: Partial<AutoPathwayConfig> = {},
  initialRootNode?: ConsultantNode
): SimulationResult {
  const config: AutoPathwayConfig = {
    ...DEFAULT_AUTO_PATHWAY_CONFIG,
    ...customConfig,
  };

  const totalMonths = Math.max(1, Math.min(60, config.months || 12));
  let nodeSequence = 1;
  const stateMap = new Map<string, InternalConsultantState>();

  const effectiveConversionRate =
    customConfig.meetingsPerContract !== undefined
      ? 1 / customConfig.meetingsPerContract
      : customConfig.conversionRate !== undefined
      ? customConfig.conversionRate
      : config.meetingsPerContract !== undefined
      ? 1 / config.meetingsPerContract
      : config.conversionRate ?? 0.10;

  const effectiveMeetingsPerContract =
    customConfig.meetingsPerContract !== undefined
      ? customConfig.meetingsPerContract
      : customConfig.conversionRate !== undefined
      ? Math.max(1, Math.round(1 / customConfig.conversionRate))
      : config.meetingsPerContract !== undefined
      ? config.meetingsPerContract
      : effectiveConversionRate > 0
      ? Math.max(1, Math.round(1 / effectiveConversionRate))
      : 10;

  const effectiveContractSizeAE =
    customConfig.averageContractAE ??
    customConfig.contractSizeAE ??
    config.averageContractAE ??
    config.contractSizeAE ??
    50.0;

  const preOnboardingAttrition =
    customConfig.preOnboardingAttritionRate !== undefined
      ? customConfig.preOnboardingAttritionRate
      : config.preOnboardingAttritionRate !== undefined
      ? config.preOnboardingAttritionRate
      : 0.30;

  let rootNode: ConsultantNode;

  if (initialRootNode) {
    rootNode = cloneTree(initialRootNode);

    // Bottom-up initialization of baselines and initial structural totals for transferred nodes
    function initializeBaselines(node: ConsultantNode) {
      for (const child of node.children) {
        initializeBaselines(child);
      }
      const childrenTotalAE = node.children.reduce((sum, c) => sum + (c.totalCumulativeAE ?? 0), 0);
      const userTeamAE = node.teamCumulativeAE ?? 0;
      // If the user specified team units higher than what the modeled children account for,
      // preserve that unmodeled difference as the baseline throughout the simulation.
      node.baselineTeamCumulativeAE = Math.max(0, userTeamAE - childrenTotalAE);
      node.teamCumulativeAE = node.baselineTeamCumulativeAE + childrenTotalAE;
      node.totalCumulativeAE = Math.round(((node.personalCumulativeAE ?? 0) + node.teamCumulativeAE) * 10) / 10;
    }
    initializeBaselines(rootNode);

    // Initial post-order evaluation of qualifications
    const evaluatedInitial = evaluateTreeQualifications(rootNode);
    function syncInitial(target: ConsultantNode, source: ConsultantNode) {
      target.level = source.level;
      target.teamMonthlyAE = source.teamMonthlyAE;
      target.teamCumulativeAE = source.teamCumulativeAE;
      target.totalMonthlyAE = source.totalMonthlyAE;
      target.totalCumulativeAE = source.totalCumulativeAE;
      for (let i = 0; i < target.children.length; i++) {
        if (source.children[i]) {
          syncInitial(target.children[i], source.children[i]);
        }
      }
    }
    syncInitial(rootNode, evaluatedInitial);

    const existingNodes = flattenTree(rootNode);

    // Initialize sequence higher than any existing numeric node ID
    for (const n of existingNodes) {
      const match = n.id.match(/^node-(\d+)$/);
      if (match) {
        const val = parseInt(match[1], 10);
        if (val >= nodeSequence) {
          nodeSequence = val + 1;
        }
      }
      // Enforce strict career level qualification on transferred nodes:
      // A consultant cannot hold Level 3+ if they have < 700 total AE or < 300 personal AE.
      if (n.level >= 3 && (n.totalCumulativeAE < 700 || n.personalCumulativeAE < 300)) {
        n.level = n.totalCumulativeAE >= 100 ? 2 : 1;
      } else if (n.level === 2 && n.totalCumulativeAE < 100) {
        n.level = 1;
      }

      if (n.joinedMonth === undefined) {
        if ((n.personalCumulativeAE ?? 0) >= 200) {
          n.joinedMonth = -1; // Established experienced partner: ready to recruit immediately
        } else {
          n.joinedMonth = 0; // Active new consultant in team: ready to recruit immediately
        }
      }
      stateMap.set(n.id, { recruitPool: 0, meetingPool: 0 });
    }
  } else {
    // Initialize root consultant (The User / Newbie)
    rootNode = {
      id: `node-${nodeSequence++}`,
      name: 'Ви (Лідер)',
      parentId: null,
      level: 1,
      personalCumulativeAE: 0,
      personalMonthlyAE: 0,
      teamCumulativeAE: 0,
      teamMonthlyAE: 0,
      totalCumulativeAE: 0,
      totalMonthlyAE: 0,
      monthlyMeetings: config.leaderPersonalMeetings ?? config.newbieMeetingsPerMonth,
      conversionRate: effectiveConversionRate,
      recruitmentRate: config.leaderRecruitmentPace ?? config.recruitmentPace,
      isActive: true,
      joinedMonth: 1,
      children: [],
      baselineTeamCumulativeAE: 0,
    };
    stateMap.set(rootNode.id, { recruitPool: 0, meetingPool: 0 });
  }

  const steps: MonthlySimulationStep[] = [];

  for (let month = 1; month <= totalMonths; month++) {
    // 1. Traverse all nodes currently in tree to determine activity & personal production
    function processNodeProduction(node: ConsultantNode) {
      const isRoot = node.parentId === null;
      const tenure = month - (node.joinedMonth ?? 1) + 1;

      let state = stateMap.get(node.id);
      if (!state) {
        state = { recruitPool: 0, meetingPool: 0 };
        stateMap.set(node.id, state);
      }

      if (isRoot) {
        node.isActive = true;
      } else if (tenure > 1) {
        node.isActive = isNodeActiveDeterministic(node.id, month, config.attritionRate);
      } else {
        node.isActive = true;
      }

      if (node.isActive) {
        let meetings = 0;
        if (isRoot) {
          // Check if leader ceased personal sales from this level
          const hasCeased =
            config.leaderCeaseLevel &&
            config.leaderCeaseLevel > 0 &&
            node.level >= config.leaderCeaseLevel;

          if (hasCeased) {
            meetings = 0;
          } else if (config.leaderPersonalMeetings !== undefined) {
            meetings = config.leaderPersonalMeetings;
          } else {
            meetings = tenure <= 3 ? config.newbieMeetingsPerMonth : config.leaderMeetingsPerMonth;
          }
        } else {
          // Check if downline team consultant ceased personal sales from this cutoff level
          const hasCeased =
            config.teamCeaseLevel &&
            config.teamCeaseLevel > 0 &&
            node.level >= config.teamCeaseLevel;

          if (hasCeased) {
            meetings = 0;
          } else {
            // Newbie vs Veteran: < 200 personal units = Newbie, >= 200 personal units = Experienced
            const isVeteran = (node.personalCumulativeAE ?? 0) >= 200;
            meetings = isVeteran ? config.leaderMeetingsPerMonth : config.newbieMeetingsPerMonth;
          }
        }

        node.monthlyMeetings = meetings;
        node.conversionRate = effectiveConversionRate;

        // Discrete contracts with persistent carryover pool:
        // Only whole contracts are signed when accumulated meetings reach or exceed effectiveMeetingsPerContract.
        // Remainder meetings carry over to the subsequent month.
        state.meetingPool += meetings;
        const wholeContracts = Math.floor(state.meetingPool / effectiveMeetingsPerContract);
        state.meetingPool -= wholeContracts * effectiveMeetingsPerContract;

        node.personalMonthlyAE = wholeContracts * effectiveContractSizeAE;
        node.personalCumulativeAE = Math.round((node.personalCumulativeAE + node.personalMonthlyAE) * 10) / 10;
      } else {
        node.personalMonthlyAE = 0;
      }

      for (const child of node.children) {
        processNodeProduction(child);
      }
    }

    processNodeProduction(rootNode);

    // 2. Duplication & Recruitment:
    // Leader personal recruitment is independent (leaderRecruitmentPace, ceased at leaderCeaseLevel).
    // Downline Team recruitment:
    // - Before leader reaches Level 4 (rootNode.level < 4): fixed recruitmentPace.
    // - Once leader reaches Level 4+ (rootNode.level >= 4): percentage of total structure size (% of team at seminar).
    const isLeaderL4Plus = rootNode.level >= 4;
    const eligibleDownlineRecruiterIds = new Set<string>();
    let downlineGrossPacePerNode = 0;

    if (isLeaderL4Plus) {
      function findDownlineRecruiters(n: ConsultantNode, d: number) {
        if (n.parentId !== null && n.isActive && d <= 3) {
          const t = month - (n.joinedMonth ?? 1) + 1;
          if (t >= 2 || (n.personalCumulativeAE ?? 0) >= 50) {
            eligibleDownlineRecruiterIds.add(n.id);
          }
        }
        for (const child of n.children) {
          findDownlineRecruiters(child, d + 1);
        }
      }
      findDownlineRecruiters(rootNode, 0);

      // Fallback: if all active downline partners are in month 1, include them
      if (eligibleDownlineRecruiterIds.size === 0) {
        function findAnyActiveDownline(n: ConsultantNode, d: number) {
          if (n.parentId !== null && n.isActive && d <= 3) {
            eligibleDownlineRecruiterIds.add(n.id);
          }
          for (const child of n.children) {
            findAnyActiveDownline(child, d + 1);
          }
        }
        findAnyActiveDownline(rootNode, 0);
      }

      const totalStructureSize = countTotalConsultants(rootNode);
      const seminarPercent = config.teamSeminarRecruitPercent ?? 20;
      const seminarRate = seminarPercent > 1 ? seminarPercent / 100 : seminarPercent;
      const totalGrossTeamRecruits = totalStructureSize * seminarRate;

      if (eligibleDownlineRecruiterIds.size > 0) {
        downlineGrossPacePerNode = totalGrossTeamRecruits / eligibleDownlineRecruiterIds.size;
      }
    }

    function processRecruitment(node: ConsultantNode, depth: number) {
      const isRoot = node.parentId === null;
      const tenure = month - (node.joinedMonth ?? 1) + 1;

      let state = stateMap.get(node.id);
      if (!state) {
        state = { recruitPool: 0, meetingPool: 0 };
        stateMap.set(node.id, state);
      }

      const existingChildren = [...node.children];

      if (node.isActive && depth <= 3) {
        let monthlyPace = 0;
        if (isRoot) {
          const hasCeased =
            config.leaderCeaseLevel &&
            config.leaderCeaseLevel > 0 &&
            node.level >= config.leaderCeaseLevel;

          if (hasCeased) {
            monthlyPace = 0;
          } else if (config.leaderRecruitmentPace !== undefined) {
            monthlyPace = config.leaderRecruitmentPace;
          } else {
            monthlyPace = config.recruitmentPace;
          }
        } else if (isLeaderL4Plus) {
          // Team recruitment scaled from total structure at Level 4+
          monthlyPace = eligibleDownlineRecruiterIds.has(node.id) ? downlineGrossPacePerNode : 0;
        } else if (tenure >= 2 || (node.personalCumulativeAE ?? 0) >= 50) {
          // Downline duplication: uses recruitmentPace directly without artificial 0.4 throttling
          monthlyPace = config.recruitmentPace * (config.downlineDuplicationFactor ?? 1.0);
        }

        const effectivePace = monthlyPace * (1 - preOnboardingAttrition);
        state.recruitPool += effectivePace;
        const newRecruitsCount = Math.floor(state.recruitPool);
        if (newRecruitsCount > 0) {
          state.recruitPool -= newRecruitsCount;

          for (let i = 0; i < newRecruitsCount; i++) {
            const newNodeId = `node-${nodeSequence++}`;
            const initialPolicyAE = effectiveContractSizeAE;
            const newNode: ConsultantNode = {
              id: newNodeId,
              name: getConsultantName(nodeSequence - 1),
              parentId: node.id,
              level: 1,
              personalCumulativeAE: initialPolicyAE,
              personalMonthlyAE: initialPolicyAE,
              teamCumulativeAE: 0,
              teamMonthlyAE: 0,
              totalCumulativeAE: initialPolicyAE,
              totalMonthlyAE: initialPolicyAE,
              monthlyMeetings: config.newbieMeetingsPerMonth,
              conversionRate: effectiveConversionRate,
              recruitmentRate: isLeaderL4Plus
                ? Math.max(config.recruitmentPace, downlineGrossPacePerNode)
                : config.recruitmentPace * (config.downlineDuplicationFactor ?? 1.0),
              isActive: true,
              joinedMonth: month,
              children: [],
              baselineTeamCumulativeAE: 0,
            };

            node.children.push(newNode);
            stateMap.set(newNodeId, { recruitPool: 0, meetingPool: 0 });
          }
        }
      }

      // Process only pre-existing children recursively
      for (const child of existingChildren) {
        processRecruitment(child, depth + 1);
      }
    }

    processRecruitment(rootNode, 0);

    // 3. Post-Order evaluation of Structural Turnover and Career Levels
    const updatedRoot = evaluateTreeQualifications(rootNode);
    function syncEvaluatedTree(target: ConsultantNode, source: ConsultantNode) {
      target.level = source.level;
      target.teamMonthlyAE = source.teamMonthlyAE;
      target.teamCumulativeAE = source.teamCumulativeAE;
      target.totalMonthlyAE = source.totalMonthlyAE;
      target.totalCumulativeAE = source.totalCumulativeAE;
      for (let i = 0; i < target.children.length; i++) {
        if (source.children[i]) {
          syncEvaluatedTree(target.children[i], source.children[i]);
        }
      }
    }
    syncEvaluatedTree(rootNode, updatedRoot);

    // 4. Calculate Payout for root consultant (defaults to 100% full commission)
    const rateCoeff = config.commissionRateCoefficient ?? 1.0;
    const payoutBreakdown = calculateNodePayout(rootNode, config.usdExchangeRate, rateCoeff);

    // 5. Record Month Step
    const activeCount = countActiveConsultants(rootNode);
    const totalCount = countTotalConsultants(rootNode);

    steps.push({
      month,
      rootConsultant: cloneTree(rootNode),
      payoutBreakdown,
      activeConsultantsCount: activeCount,
      totalConsultantsCount: totalCount,
      totalStructuralTurnoverAE: rootNode.totalMonthlyAE,
      monthlyPersonalAE: rootNode.personalMonthlyAE,
      cumTotalAE: rootNode.totalCumulativeAE,
      cumPersonalAE: rootNode.personalCumulativeAE,
    });
  }

  const lastStep = steps[steps.length - 1];

  return {
    steps,
    totalMonths,
    finalLevel: lastStep.rootConsultant.level,
    finalMonthlyIncomeUAH: lastStep.payoutBreakdown.totalGrossIncomeUAH,
    finalMonthlyIncomeUSD: lastStep.payoutBreakdown.totalGrossIncomeUSD,
    finalTeamSize: lastStep.totalConsultantsCount,
    finalActivePartners: lastStep.activeConsultantsCount,
    finalCumulativeAE: lastStep.cumTotalAE,
  };
}
